import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../api";
import "./ActionPlanPanel.css";

interface ActionItem {
  id: number;
  cost_center: string;
  fiscal_year: string;
  month: string;
  issue_text: string | null;
  action_text: string;
  category: string | null;
  account_number: string | null;
  closeout_milestone: string | null;
  status: "OPEN" | "CLOSED";
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface AccountInfo {
  account: string;
  category: string;
}

interface ActionPlanPanelProps {
  open: boolean;
  onClose: () => void;
  costCenterNumber: string;
  costCenterName?: string;
  fiscalYear: string;
  month: string;
  categories: string[];
  accounts?: AccountInfo[];
}

const MAX_ACTIONS = 5;
const CATEGORIES = ["COMPENSATION", "OCCUPANCY", "OPERATING", "ADVERTISING", "ALLOCATED"];
const ActionPlanPanel: React.FC<ActionPlanPanelProps> = ({
  open, onClose, costCenterNumber, costCenterName, fiscalYear, month, categories, accounts = [],
}) => {
  const [actions, setActions] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [copied, setCopied] = useState(false);
  const debounceTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const fetchActions = useCallback(async () => {
    if (!costCenterNumber || !fiscalYear || !month) return;
    setLoading(true);
    try {
      const res = await api.get("/action-plans", {
        params: { cost_center: costCenterNumber, fy: fiscalYear, month },
      });
      setActions(res.data);
    } catch (err) {
      console.error("Failed to fetch action plans:", err);
    } finally {
      setLoading(false);
    }
  }, [costCenterNumber, fiscalYear, month]);

  useEffect(() => {
    if (open) fetchActions();
  }, [open, fetchActions]);

  useEffect(() => {
    const timers = debounceTimers.current;
    return () => { Object.values(timers).forEach(clearTimeout); };
  }, []);

  const handleAdd = async () => {
    if (actions.length >= MAX_ACTIONS || adding) return;
    setAdding(true);
    try {
      const res = await api.post("/action-plans", {
        cost_center: costCenterNumber,
        fiscal_year: fiscalYear,
        month,
        action_text: "",
      });
      setActions((prev) => {
        if (prev.length >= MAX_ACTIONS) return prev;
        return [...prev, res.data];
      });
    } catch (err: any) {
      if (err?.response?.status === 400) {
        fetchActions();
      }
    } finally {
      setAdding(false);
    }
  };

  const handleUpdate = async (id: number, field: string, value: string) => {
    setActions((prev) => prev.map((a) => (a.id === id ? { ...a, [field]: value } : a)));
    try {
      await api.put(`/action-plans/${id}`, { [field]: value });
    } catch {
      fetchActions();
    }
  };

  const handleDebouncedField = (id: number, field: "issue_text" | "action_text", value: string) => {
    setActions((prev) => prev.map((a) => (a.id === id ? { ...a, [field]: value } : a)));
    const key = `${id}_${field}`;
    if (debounceTimers.current[key as any]) clearTimeout(debounceTimers.current[key as any]);
    (debounceTimers.current as any)[key] = setTimeout(() => {
      api.put(`/action-plans/${id}`, { [field]: value.trim() }).catch(() => fetchActions());
    }, 600);
  };

  const handleDelete = async (id: number) => {
    setActions((prev) => prev.filter((a) => a.id !== id));
    try {
      await api.delete(`/action-plans/${id}`);
    } catch {
      fetchActions();
    }
  };

  const handleToggleStatus = (action: ActionItem) => {
    handleUpdate(action.id, "status", action.status === "OPEN" ? "CLOSED" : "OPEN");
  };

  const handleShare = () => {
    if (actions.length === 0) {
      navigator.clipboard.writeText("No actions recorded.");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    }

    // Sort by close date: earliest first, no-date items last
    const sorted = [...actions].sort((a, b) => {
      if (!a.closeout_milestone && !b.closeout_milestone) return 0;
      if (!a.closeout_milestone) return 1;
      if (!b.closeout_milestone) return -1;
      return a.closeout_milestone.localeCompare(b.closeout_milestone);
    });

    const fmtDate = (d: string | null) =>
      d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

    const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    const thStyle = 'style="background:#2d3748;color:#fff;padding:6px 10px;font-size:11px;font-weight:700;text-align:left;border:1px solid #4a5568"';
    const tdStyle = 'style="padding:5px 10px;font-size:11px;border:1px solid #e2e8f0"';

    const headerCols = ["#", "Issue", "Action", "Category", "Account", "Closeout Target", "Status"];
    const thead = `<tr>${headerCols.map(h => `<th ${thStyle}>${h}</th>`).join("")}</tr>`;
    const tbody = sorted.map((a, i) => {
      const cells = [
        String(i + 1),
        esc(a.issue_text || ""),
        esc(a.action_text || "(no description)"),
        esc(a.category || ""),
        esc(a.account_number || ""),
        fmtDate(a.closeout_milestone),
        a.status,
      ];
      return `<tr>${cells.map(c => `<td ${tdStyle}>${c}</td>`).join("")}</tr>`;
    }).join("");

    const html = `<table style="border-collapse:collapse;font-family:Calibri,Arial,sans-serif">${thead}${tbody}</table>`;

    // Plain text fallback (tab-separated)
    const T = "\t";
    const plain = [
      headerCols.join(T),
      ...sorted.map((a, i) =>
        [String(i + 1), a.issue_text || "", a.action_text || "(no description)", a.category || "", a.account_number || "", fmtDate(a.closeout_milestone), a.status].join(T)
      ),
    ].join("\n");

    const htmlBlob = new Blob([html], { type: "text/html" });
    const textBlob = new Blob([plain], { type: "text/plain" });

    navigator.clipboard.write([
      new ClipboardItem({ "text/html": htmlBlob, "text/plain": textBlob }),
    ]).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  /** Get accounts filtered by the action's selected category */
  const getFilteredAccounts = (action: ActionItem): AccountInfo[] => {
    if (!action.category) return accounts;
    return accounts.filter(a => a.category === action.category);
  };

  if (!open) return null;

  const openCount = actions.filter((a) => a.status === "OPEN").length;
  const closedCount = actions.filter((a) => a.status === "CLOSED").length;
  const catOptions = categories.length > 0 ? categories : CATEGORIES;
  const atLimit = actions.length >= MAX_ACTIONS;

  return (
    <>
      <div className="ap-overlay" onClick={onClose} />
      <div className="ap-panel">
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="ap-header">
          <div className="ap-header-top">
            <div>
              <div className="ap-header-eyebrow">Action Plan</div>
              <div className="ap-title">{month} {fiscalYear}</div>
              <div className="ap-subtitle">
                CC {costCenterNumber}{costCenterName ? ` — ${costCenterName}` : ""}
              </div>
            </div>
            <button className="ap-close" onClick={onClose}>
              <span className="material-icons">close</span>
            </button>
          </div>
          <div className="ap-stats">
            <div className="ap-stat">
              <span className="ap-stat-val">{actions.length}</span>
              <span className="ap-stat-label">of {MAX_ACTIONS}</span>
            </div>
            {openCount > 0 && (
              <div className="ap-stat ap-stat--open">
                <span className="ap-stat-val">{openCount}</span>
                <span className="ap-stat-label">open</span>
              </div>
            )}
            {closedCount > 0 && (
              <div className="ap-stat ap-stat--closed">
                <span className="ap-stat-val">{closedCount}</span>
                <span className="ap-stat-label">closed</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Body ───────────────────────────────────────────────── */}
        <div className="ap-body">
          {loading && actions.length === 0 && (
            <div className="ap-empty">Loading...</div>
          )}
          {!loading && actions.length === 0 && (
            <div className="ap-empty">
              <span className="material-icons ap-empty-icon">fact_check</span>
              <div className="ap-empty-text">No actions for {month}</div>
              <div className="ap-empty-hint">Add up to {MAX_ACTIONS} action items to track</div>
            </div>
          )}
          {actions.map((action, idx) => {
            const filteredAccounts = getFilteredAccounts(action);
            return (
              <div
                key={action.id}
                className={`ap-card ${action.status === "CLOSED" ? "ap-card--closed" : ""}`}
              >
                <button className="ap-delete" onClick={() => handleDelete(action.id)} title="Delete">
                  <span className="material-icons">close</span>
                </button>
                <div className="ap-card-top">
                  <span className="ap-card-num">{idx + 1}</span>
                  <button
                    className={`ap-status ap-status--${action.status.toLowerCase()}`}
                    onClick={() => handleToggleStatus(action)}
                    title={`Click to ${action.status === "OPEN" ? "close" : "reopen"}`}
                  >
                    <span className="material-icons" style={{ fontSize: 13 }}>
                      {action.status === "OPEN" ? "radio_button_unchecked" : "check_circle"}
                    </span>
                    {action.status}
                  </button>
                  <div className="ap-date-group">
                    <span className="ap-date-label">Target</span>
                    <input
                      type="date"
                      className="ap-date-input"
                      value={action.closeout_milestone || ""}
                      onChange={(e) => handleUpdate(action.id, "closeout_milestone", e.target.value || "")}
                    />
                  </div>
                </div>
                <div className="ap-field">
                  <span className="ap-field-label">Issue</span>
                  <input
                    className="ap-issue-input"
                    value={action.issue_text || ""}
                    onChange={(e) => handleDebouncedField(action.id, "issue_text", e.target.value)}
                    placeholder="What is the problem? e.g. Contractors overspent $300K"
                    maxLength={500}
                  />
                </div>
                <div className="ap-field">
                  <span className="ap-field-label">Action</span>
                  <textarea
                    className="ap-card-textarea"
                    value={action.action_text}
                    onChange={(e) => handleDebouncedField(action.id, "action_text", e.target.value)}
                    placeholder="Plan to mitigate the issue..."
                    rows={2}
                    maxLength={500}
                  />
                </div>
                <div className="ap-card-bottom">
                  <select
                    className="ap-bottom-select"
                    value={action.category || ""}
                    onChange={(e) => {
                      handleUpdate(action.id, "category", e.target.value || "");
                      if (action.account_number && e.target.value) {
                        const acctInfo = accounts.find(a => a.account === action.account_number);
                        if (acctInfo && acctInfo.category !== e.target.value) {
                          handleUpdate(action.id, "account_number", "");
                        }
                      }
                    }}
                  >
                    <option value="">Category</option>
                    {catOptions.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <select
                    className="ap-bottom-select ap-bottom-select--wide"
                    value={action.account_number || ""}
                    onChange={(e) => handleUpdate(action.id, "account_number", e.target.value || "")}
                  >
                    <option value="">Account</option>
                    {filteredAccounts.map((a) => (
                      <option key={a.account} value={a.account}>{a.account}</option>
                    ))}
                  </select>
                  {action.created_by && (
                    <span className="ap-card-author">{action.created_by}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Footer ─────────────────────────────────────────────── */}
        <div className="ap-footer">
          <button
            className="ap-add-btn"
            onClick={handleAdd}
            disabled={atLimit || adding}
          >
            <span className="material-icons">add</span>
            {adding ? "Adding..." : atLimit ? `${MAX_ACTIONS} / ${MAX_ACTIONS} Actions` : "Add Action"}
          </button>
          {actions.length > 0 && (
            <button className="ap-share-btn" onClick={handleShare}>
              <span className="material-icons">{copied ? "check" : "content_copy"}</span>
              {copied ? "Copied" : "Copy for Meeting"}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default ActionPlanPanel;
